'use client'

import React from 'react'
import { Box, Flex, Text } from '@mantine/core'
import { motion } from 'framer-motion'
import {
    IconCircleCheck,
    IconMapPin,
    IconAward,
    IconCoin,
    IconCalendar,
    IconMessage,
    IconShieldCheck,
    IconClock,
    type IconProps,
} from '@tabler/icons-react'
import { staggerContainer, staggerItem } from '@/lib/animations'
import type { GreenSectionIconKey } from '@/types/enums/sku-component-types'

type GreenSectionIcon = React.ComponentType<IconProps>

const ICON_BY_KEY: Record<GreenSectionIconKey, GreenSectionIcon> = {
    circleCheck: IconCircleCheck,
    mapPin: IconMapPin,
    award: IconAward,
    coin: IconCoin,
    calendar: IconCalendar,
    message: IconMessage,
    shield: IconShieldCheck,
    clock: IconClock,
}

const DEFAULT_ITEM_ICON = IconCircleCheck

const DEFAULT_GREEN_SECTION_ITEMS: { icon: GreenSectionIcon; label: string }[] = [
    { icon: IconCircleCheck, label: 'Most insurance accepted' },
    { icon: IconAward, label: 'Board-certified clinicians' },
    { icon: IconCoin, label: 'No hidden fees' },
    { icon: IconMapPin, label: 'Available nationwide' },
]

/** CMS-driven trust bar. Falls back to the default chips when `items` is omitted/empty. */
export function GreenSection({ items }: { items?: { label: string; icon?: GreenSectionIconKey }[] }) {
    const resolvedItems: { icon: GreenSectionIcon; label: string }[] =
        items && items.length
            ? items.map((item) => ({
                  icon: (item.icon && ICON_BY_KEY[item.icon]) || DEFAULT_ITEM_ICON,
                  label: item.label,
              }))
            : DEFAULT_GREEN_SECTION_ITEMS

    return (
        <Box style={{ width: '100%' }}>
            <Flex w="100%" direction="column" gap={24} align="center">
                <Flex
                    component={motion.div}
                    variants={staggerContainer}
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true, margin: '-50px 0px' }}
                    gap={{ base: 16, sm: 32 }}
                    wrap="wrap"
                    align="center"
                    justify="center"
                    w="100%"
                    px={24}
                    py={20}
                    style={{
                        borderRadius: 24,
                        background: 'rgba(22, 179, 100, 0.15)',
                    }}
                >
                    {resolvedItems.map(({ icon: Icon, label }) => (
                        <Flex key={label} component={motion.div} variants={staggerItem} gap={8} align="center">
                            <Icon size={20} color="#087442" stroke={2} />
                            <Text size="sm" fw={550} lh="20px" c="green.7">
                                <span>{label}</span>
                            </Text>
                        </Flex>
                    ))}
                </Flex>
            </Flex>
        </Box>
    )
}
